import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { formatPercent, formatMinutesAsTime } from '@/utils/analyticsHelpers';
import { useVehicleYearGroupEfficiency } from '@/hooks/kpi/useVehicleYearGroupEfficiency';
import { useVehicleYearGroupTechEfficiency } from '@/hooks/kpi/useVehicleYearGroupTechEfficiency';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface YearGroupPerformanceProps {
  shopId: string;
  startDate: string;
  endDate: string;
  shopTimezone: string;
}

interface YearGroupStats {
  yearGroup: string;
  totalBookMinutes: number;
  totalActualMinutes: number;
  ticketCount: number;
  efficiency: number | null;
}

interface TechStats {
  techId: string;
  techName: string;
  totalBookMinutes: number;
  totalActualMinutes: number;
  ticketCount: number;
  efficiency: number | null;
}

export function YearGroupPerformance({ shopId, startDate, endDate, shopTimezone }: YearGroupPerformanceProps) {
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  // K-T-11: top-view aggregation sourced from canonical
  // `kpi_efficiency_by_vehicle_year_group` RPC.
  const { data: rawYearRows, isLoading: loading } = useVehicleYearGroupEfficiency({
    shopId,
    startDate,
    endDate,
    shopTimezone,
  });

  // K-T-12: per-tech drill-down within the expanded year bucket
  // sourced from canonical `kpi_efficiency_by_vehicle_year_group_tech`
  // RPC. Replaces the prior local vehicles + tickets + technicians
  // join and inline SUM/SUM math. The hook parses the bucket label
  // (en-dash separator) into start/end years internally — single
  // source of truth for the parse, mirroring the pre-K-T-12 panel
  // logic. Behavior change: techs with fewer than 3 qualifying tickets
  // now render an "—" placeholder, matching K-T-6..K-T-11 convention.
  const { data: rawTechRows, isLoading: loadingDrilldown } = useVehicleYearGroupTechEfficiency({
    shopId,
    startDate,
    endDate,
    shopTimezone,
    yearGroup: expandedGroup,
  });

  const yearGroupStats: YearGroupStats[] = (rawYearRows ?? [])
    .map((r) => ({
      yearGroup: r.year_group,
      totalBookMinutes: r.total_book_minutes,
      totalActualMinutes: r.total_actual_minutes,
      ticketCount: r.ticket_count,
      // Canonical RPC returns ratio as a fraction (e.g. 0.92);
      // the panel displays as a percent-rounded number to match the
      // pre-K-T-11 `calculateEfficiency` output convention.
      efficiency: r.gated || r.ratio === null
        ? null
        : Math.round(r.ratio * 100),
    }))
    .sort((a, b) => {
      // Sort by year group chronologically (newest first).
      const aYear = parseInt(a.yearGroup.split('–')[0]);
      const bYear = parseInt(b.yearGroup.split('–')[0]);
      return bYear - aYear;
    });

  const techDrilldown: TechStats[] = (rawTechRows ?? [])
    .map((r) => ({
      techId: r.tech_id,
      techName: r.tech_name,
      totalBookMinutes: r.total_book_minutes,
      totalActualMinutes: r.total_actual_minutes,
      ticketCount: r.ticket_count,
      efficiency: r.gated || r.ratio === null
        ? null
        : Math.round(r.ratio * 100),
    }))
    .sort((a, b) => (b.efficiency || 0) - (a.efficiency || 0));

  function handleGroupClick(yearGroup: string) {
    setExpandedGroup(expandedGroup === yearGroup ? null : yearGroup);
  }

  function getEfficiencyColor(efficiency: number | null): string {
    if (efficiency === null) return '#6b7280'; // Gray
    if (efficiency >= 100) return '#22c55e'; // Bright green
    if (efficiency >= 80) return '#eab308'; // Bright yellow
    return '#ef4444'; // Bright red
  }

  const chartData = yearGroupStats.map(g => ({
    name: g.yearGroup,
    efficiency: g.efficiency || 0,
  }));

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Year Group Performance</CardTitle>
        <CardDescription>
          Efficiency by vehicle year (5-year buckets)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {yearGroupStats.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No completed tickets in this date range</p>
        ) : (
          <>
            {/* Bar Chart */}
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(value: number) => [`${value}%`, 'Efficiency']} />
                  <Bar dataKey="efficiency" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fill={getEfficiencyColor(entry.efficiency)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Year Group</TableHead>
                  <TableHead className="text-right">Tickets</TableHead>
                  <TableHead className="text-right">Book Time</TableHead>
                  <TableHead className="text-right">Actual Time</TableHead>
                  <TableHead className="text-right">Efficiency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {yearGroupStats.map((group) => (
                  <>
                    <TableRow 
                      key={group.yearGroup}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleGroupClick(group.yearGroup)}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {expandedGroup === group.yearGroup ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          {group.yearGroup}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{group.ticketCount}</TableCell>
                      <TableCell className="text-right">{formatMinutesAsTime(group.totalBookMinutes)}</TableCell>
                      <TableCell className="text-right">{formatMinutesAsTime(group.totalActualMinutes)}</TableCell>
                      <TableCell className="text-right">
                        <Badge 
                          variant={group.efficiency && group.efficiency >= 100 ? 'default' : 'secondary'}
                          className={group.efficiency && group.efficiency >= 100 ? 'bg-green-500/20 text-green-700 dark:text-green-300' : ''}
                        >
                          {formatPercent(group.efficiency)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {expandedGroup === group.yearGroup && (
                      <TableRow>
                        <TableCell colSpan={5} className="bg-muted/30 p-4">
                          {loadingDrilldown ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                          ) : techDrilldown.length === 0 ? (
                            <p className="text-muted-foreground text-sm text-center">No technician data</p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Technician</TableHead>
                                  <TableHead className="text-right">Tickets</TableHead>
                                  <TableHead className="text-right">Book</TableHead>
                                  <TableHead className="text-right">Actual</TableHead>
                                  <TableHead className="text-right">Efficiency</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {techDrilldown.map((tech) => (
                                  <TableRow key={tech.techId}>
                                    <TableCell>{tech.techName}</TableCell>
                                    <TableCell className="text-right">{tech.ticketCount}</TableCell>
                                    <TableCell className="text-right">{formatMinutesAsTime(tech.totalBookMinutes)}</TableCell>
                                    <TableCell className="text-right">{formatMinutesAsTime(tech.totalActualMinutes)}</TableCell>
                                    <TableCell className="text-right">
                                      <Badge variant="outline">{formatPercent(tech.efficiency)}</Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}
